import PropTypes from "prop-types"
import React from "react"
import { userdataStorage } from "QuorumGrassroots/framework/helpers/userdataStorageManager"
import styled from "styled-components"
import { getFontColorForBackgroundContrast } from "QuorumGrassroots/styled-components/helperFunctions"
import styleWithOrgDesign from "QuorumGrassroots/styled-components/connect"
import { Icon } from "quorumdesign"
import { constants } from "QuorumGrassroots/constants"
import i18n from "i18n"

const StyledUserInfoBlock = styleWithOrgDesign(styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: Roboto;
    border-color: ${(props) => getFontColorForBackgroundContrast(props.organizationDesign.primary_color)};
    background-color: transparent;
    color: ${(props) => getFontColorForBackgroundContrast(props.organizationDesign.primary_color)};
    padding-bottom: ${(props) => props.paddingBottom || "0"};

    // Conditional color logic based on campaign type
    ${(props) => {
        const { CampaignType } = DjangIO.app.grassroots.campaign.types
        const isInteractiveCampaign = props.campaignType === CampaignType.personalized_messages.value

        // For interactive campaigns, always use gray color
        if (isInteractiveCampaign) {
            return `
                border-color: ${constants.colors.gray[8]};
                color: ${constants.colors.gray[8]};
                border-bottom: 1px solid #E9ECEF;
                padding-bottom: 1rem;
            `
        } else {
            return `
                color: ${getFontColorForBackgroundContrast(props.organizationDesign.primary_color)};
            `
        }
    }}

    .user-info-left {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding-right: 1rem;
        @media (max-width: 768px) {
            padding-right: 0;
        }
    }

    .profile-icon {
        width: 1.25rem;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        font-size: 1.125rem;
        color: currentColor;
    }

    .user-details {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
    }

    .user-name {
        font-weight: 500;
        font-size: 1rem;
        line-height: 1rem;
        color: currentColor;
    }

    .user-address {
        font-size: 0.85rem;
        line-height: 0.85rem;
        color: currentColor;
    }

    .user-info-right {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .update-link {
        font-size: 0.75rem;
        background: none;
        border: none;
        cursor: pointer;
        color: currentColor;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0;
        font-family: inherit;
        text-decoration: none;

        .button-label {
            text-decoration: underline;
            white-space: nowrap;
        }

        &:hover {
            text-decoration: none;
            .button-label {
                text-decoration: none;
            }
        }
    }

    .arrow-icon {
        font-size: 0.75rem;
        color: currentColor;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;

        .user-info-left {
            align-items: center;
        }

        .user-info-right {
            align-self: center;
            justify-content: center;
        }
    }
`)

const UserInfoUpdateBlock = ({ campaignType }) => {
    const t = (key) => i18n.t(key)

    // Check if we have browser storage data (for returning users)
    const storedUserdata = userdataStorage.getItem("userdata")
    const hasStoredData = storedUserdata && Object.keys(storedUserdata).length > 0

    // Only show for users with browser storage (returning advocates)
    if (!hasStoredData) {
        return null
    }

    // Get user information to display
    const displayData = {
        name:
            storedUserdata.firstname && storedUserdata.lastname
                ? `${storedUserdata.firstname} ${storedUserdata.lastname}`
                : storedUserdata.firstname || storedUserdata.lastname,
        address: storedUserdata.input_address || storedUserdata.city || storedUserdata.state,
    }

    // Filter out empty values
    const filteredData = Object.entries(displayData).filter(([, value]) => value)

    // Don't show if we don't have meaningful data to display
    if (filteredData.length === 0) {
        return null
    }

    const handleUpdateClick = () => {
        // Trigger update information flow
        // This could scroll to the form or open a modal
        const updateInfoElement = document.querySelector('[data-cy="update-info"]')
        if (updateInfoElement) {
            updateInfoElement.click()
        } else {
            // Fallback: scroll to the form section
            const formSection = document.querySelector(".user-info-form-section")
            if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" })
            }
        }
    }

    return (
        <StyledUserInfoBlock className="user-info-update-block" campaignType={campaignType}>
            <div className="user-info-left">
                <div className="profile-icon">
                    <Icon icon="user-circle" iconFamily="far" />
                </div>
                <div className="user-details">
                    {displayData.name && <div className="user-name">{displayData.name}</div>}
                    {displayData.address && <div className="user-address">{displayData.address}</div>}
                </div>
            </div>
            <div className="user-info-right">
                <button className="update-link" onClick={handleUpdateClick} type="button">
                    <span className="button-label">{t("campaign.write.update_information")}</span>
                    <span className="arrow-icon">→</span>
                </button>
            </div>
        </StyledUserInfoBlock>
    )
}

UserInfoUpdateBlock.propTypes = {
    campaignType: PropTypes.number,
}

export { UserInfoUpdateBlock }
